(() => {
  const storageKey = 'os-recently-viewed-products-v1';
  const historyLimit = 24;

  function readHistory() {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string' && handle) : [];
    } catch (error) {
      return [];
    }
  }

  function writeHistory(handles) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(handles.slice(0, historyLimit)));
    } catch (error) {
      return;
    }
  }

  function normalizeImageUrl(url, width) {
    if (!url) return '';

    try {
      const absoluteUrl = url.startsWith('//') ? `https:${url}` : url;
      const imageUrl = new URL(absoluteUrl, window.location.origin);
      imageUrl.searchParams.set('width', width);
      return imageUrl.toString();
    } catch (error) {
      return url;
    }
  }

  function formatMoney(cents, moneyFormat, currency) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(Number(cents) / 100);
    } catch (error) {
      return `${(Number(cents) / 100).toFixed(2)} ${currency || 'EUR'}`;
    }
  }

  function appendTextElement(parent, tagName, className, text) {
    const element = document.createElement(tagName);
    element.className = className;
    element.textContent = text;
    parent.appendChild(element);
    return element;
  }

  function createImage(product, imageUrl, className) {
    const image = document.createElement('img');
    image.className = className;
    image.src = normalizeImageUrl(imageUrl, 720);
    image.srcset = [360, 540, 720, 960]
      .map((width) => `${normalizeImageUrl(imageUrl, width)} ${width}w`)
      .join(', ');
    image.sizes = '(max-width: 749px) 50vw, 25vw';
    image.alt = product.title || '';
    image.loading = 'lazy';
    image.decoding = 'async';
    return image;
  }

  function createCard(product, settings) {
    const item = document.createElement('li');
    item.className = 'os-recently-viewed__item';

    if (settings.showImage && product.featured_image) {
      const mediaLink = document.createElement('a');
      mediaLink.className = 'os-recently-viewed__media';
      mediaLink.href = product.url;
      mediaLink.setAttribute('aria-label', product.title);
      mediaLink.appendChild(createImage(product, product.featured_image, 'os-recently-viewed__image'));

      if (settings.showSecondaryImage && Array.isArray(product.images) && product.images[1]) {
        mediaLink.appendChild(
          createImage(product, product.images[1], 'os-recently-viewed__image os-recently-viewed__image--secondary')
        );
      }

      item.appendChild(mediaLink);
    }

    const hasContent = settings.showVendor || settings.showTitle || settings.showPrice || settings.showSoldOut;
    if (!hasContent) return item;

    const content = document.createElement('div');
    content.className = 'os-recently-viewed__content';

    if (settings.showVendor && product.vendor) {
      appendTextElement(content, 'p', 'os-recently-viewed__vendor', product.vendor);
    }

    if (settings.showTitle) {
      const title = appendTextElement(content, 'p', 'os-recently-viewed__title', '');
      const titleLink = document.createElement('a');
      titleLink.className = 'os-recently-viewed__title-link';
      titleLink.href = product.url;
      titleLink.textContent = product.title;
      title.appendChild(titleLink);
    }

    if (settings.showPrice) {
      const price = appendTextElement(
        content,
        'p',
        'os-recently-viewed__price',
        formatMoney(product.price, settings.moneyFormat, settings.currency)
      );

      if (
        settings.showComparePrice &&
        Number(product.compare_at_price) > Number(product.price)
      ) {
        const comparePrice = document.createElement('s');
        comparePrice.className = 'os-recently-viewed__compare-price';
        comparePrice.textContent = formatMoney(
          product.compare_at_price,
          settings.moneyFormat,
          settings.currency
        );
        price.appendChild(comparePrice);
      }
    }

    if (settings.showSoldOut && !product.available) {
      appendTextElement(content, 'p', 'os-recently-viewed__status', settings.soldOutLabel);
    }

    item.appendChild(content);
    return item;
  }

  async function fetchProduct(rootUrl, handle) {
    try {
      const response = await fetch(`${rootUrl}products/${encodeURIComponent(handle)}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  async function initializeSection(section) {
    if (section.dataset.osRecentlyViewedInitialized === 'true') return;
    section.dataset.osRecentlyViewedInitialized = 'true';

    const grid = section.querySelector('[data-os-recently-viewed-grid]');
    if (!grid) return;

    const currentHandle = section.dataset.currentProductHandle || '';
    const limit = Math.max(1, Number(section.dataset.productsLimit) || 4);
    const history = readHistory();
    const handlesToShow = history.filter((handle) => handle !== currentHandle).slice(0, limit);

    if (currentHandle) {
      writeHistory([currentHandle, ...history.filter((handle) => handle !== currentHandle)]);
    }

    if (!handlesToShow.length) return;

    const settings = {
      showImage: section.dataset.showImage === 'true',
      showSecondaryImage: section.dataset.showSecondaryImage === 'true',
      showVendor: section.dataset.showVendor === 'true',
      showTitle: section.dataset.showTitle === 'true',
      showPrice: section.dataset.showPrice === 'true',
      showComparePrice: section.dataset.showComparePrice === 'true',
      showSoldOut: section.dataset.showSoldOut === 'true',
      soldOutLabel: section.dataset.soldOutLabel || 'Esaurito',
      moneyFormat: section.dataset.moneyFormat || '',
      currency: section.dataset.currency || 'EUR'
    };

    const rootUrl = section.dataset.rootUrl || '/';
    const products = await Promise.all(handlesToShow.map((handle) => fetchProduct(rootUrl, handle)));
    const validProducts = products.filter(Boolean);

    if (!validProducts.length) return;

    const fragment = document.createDocumentFragment();
    validProducts.forEach((product) => fragment.appendChild(createCard(product, settings)));
    grid.replaceChildren(fragment);
    section.dataset.desktopProductCount = String(
      Math.min(validProducts.length, Number(section.dataset.desktopLimit) || validProducts.length)
    );
    section.dataset.mobileProductCount = String(
      Math.min(validProducts.length, Number(section.dataset.mobileLimit) || validProducts.length)
    );
    section.hidden = false;
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll('[data-os-recently-viewed]').forEach(initializeSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
